import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import { problemsCandidateCountQuery, problemsListQuery, type ProblemRow } from '../api/problems'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * `Problems::index` - lista de probleme, la /app/problems.
 *
 * DE CE STA IN OVERVIEW SI NU IN EASEEDESK
 *
 * Fiindca aici e locul in care CMDB-ul isi plateste intretinerea. Un registru de
 * elemente care doar tine evidenta e o cheltuiala; devine un castig cand
 * raspunde la o intrebare pe care nimeni n-o putea pune inainte - „pe ce anume
 * se sparg lucrurile la noi, in mod repetat".
 *
 * Incidentul si problema nu sunt acelasi lucru: incidentul cere restabilirea
 * serviciului ACUM, problema cere inlaturarea cauzei si are voie sa dureze doua
 * saptamani. Un server care pica in fiecare marti producea, in trei luni,
 * treisprezece tichete inchise frumos, fiecare cu SLA respectat - si nicaieri
 * propozitia „asta se repeta". Fiecare tichet arata ca un succes. Sirul lor era
 * esecul.
 *
 * DOUA CLASE DIN MACHETA VECHE NU EXISTA
 *
 * `adm-panel` si `adm-pill-danger` nu sunt definite NICAIERI in `public/css`.
 * In portal, panourile ecranului - cel care explica, cel gol, si cel cu
 * ocolirea - se desenau fara fundal, fara chenar si fara padding, iar insigna
 * „deleted item” iesea neutra tocmai unde trebuia sa fie rosie. Aici se
 * folosesc cele care exista: `adm-card` si `adm-pill-bad`.
 *
 * E singurul loc in care ecranul se abate de la macheta veche, si se abate
 * fiindca macheta aia cerea ceva ce nu i s-a dat niciodata.
 */

const STYLES = ['css/adm.css']

export function ProblemsPage() {
  const shell = useQuery(shellQuery(moduleOf('/problems')))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [company, setCompany] = useState(0)
  /* Cautarea asteapta, ca peste tot in v4: altfel „example" pleaca de sapte ori
     catre server, o data pe litera. */
  const [applied, setApplied] = useState({ search: '', status: '', company: 0 })
  useEffect(() => {
    if (search === applied.search) return
    const timer = window.setTimeout(() => setApplied((old) => ({ ...old, search })), 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const page = useQuery(
    problemsListQuery({
      search: applied.search,
      status: applied.status,
      company: applied.company,
      ci: 0,
      openOnly: false,
      start: 0,
    }),
  )

  /*
   * Numarul de tipare vine pe drumul LUI, dupa ce pagina e pe ecran. Butonul e
   * acolo de la inceput si duce unde trebuie; numarul si propozitia apar cand se
   * stiu, si daca nu se stiu, butonul ramane cum e.
   */
  const patterns = useQuery({ ...problemsCandidateCountQuery, retry: false })
  const found = typeof patterns.data?.count === 'number' ? patterns.data.count : null

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data

  if (!data.hasTables) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>
            <i className="fas fa-database"></i> Problems
          </h4>
          <p>The problem tables are not in the database yet.</p>
          <p className="text-muted small" style={{ marginBottom: 0 }}>
            {/* Un ecran gol fara explicatie s-ar citi ca „nu s-a facut nimic".
                Randul asta spune ce lipseste si cine il poate pune. */}
            This screen works as soon as the migration runs: <code>{data.migration}</code>.
          </p>
        </div>
      </Content>
    )
  }

  return (
    <Content>
      <div id="content">
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/problems/candidates"
            id="patterns-button"
            className={found !== null && found > 0 ? 'btn btn-warning btn-sm' : 'btn btn-default btn-sm'}
          >
            <i className="fas fa-search"></i> Incident patterns{' '}
            {found !== null && found > 0 && <span className="badge">{found}</span>}
          </Link>
          {found !== null && found > 0 && (
            <span className="text-muted small" style={{ marginLeft: 8 }}>
              {found === 1
                ? '1 item is collecting repeat incidents and nobody is asking why.'
                : `${found} items are collecting repeat incidents and nobody is asking why.`}
            </span>
          )}
        </div>

        <div className="form-inline" style={{ marginBottom: 16 }}>
          <div className="form-group" style={{ marginRight: 10 }}>
            <input
              type="text"
              className="form-control input-sm"
              placeholder="Title, cause or item"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginRight: 10 }}>
            <select
              className="form-control input-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setApplied((old) => ({ ...old, status: e.target.value }))
              }}
            >
              <option value="">All statuses</option>
              {data.statuses.map((pair) => (
                <option key={pair.value} value={pair.value}>
                  {pair.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginRight: 10 }}>
            <select
              className="form-control input-sm"
              value={company}
              onChange={(e) => {
                setCompany(Number(e.target.value))
                setApplied((old) => ({ ...old, company: Number(e.target.value) }))
              }}
            >
              <option value={0}>All customers</option>
              {data.companies.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {data.problems.length === 0 ? (
          <div className="adm-card">
            <p style={{ margin: 0 }}>No problem recorded.</p>
            <p className="text-muted small" style={{ margin: '6px 0 0' }}>
              {/* Gol aici nu e o veste buna, si nu se lasa sa para una. Zero
                  probleme intr-o platforma cu incidente inseamna aproape sigur
                  ca nimeni nu s-a uitat inca dincolo de incidente. */}
              That does not mean there are no root causes &mdash; it means nobody has looked for them.{' '}
              <Link to="/problems/candidates">Patterns</Link> shows where to start.
            </p>
          </div>
        ) : (
          <>
            <table className="table table-hover adm-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>#</th>
                  <th>Problem</th>
                  <th style={{ width: 180 }}>Item</th>
                  <th style={{ width: 150 }}>Status</th>
                  <th style={{ width: 110 }} className="center">
                    Incidents
                  </th>
                  <th style={{ width: 140 }}>Opened</th>
                </tr>
              </thead>
              <tbody>
                {data.problems.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to="/problems/view/$id" params={{ id: String(row.id) }}>
                        {row.id}
                      </Link>
                    </td>
                    <td>
                      <Link to="/problems/view/$id" params={{ id: String(row.id) }}>
                        {row.title}
                      </Link>{' '}
                      {row.workaround !== '' && (
                        /* Semnul ca exista o ocolire scrisa, chiar in lista. E
                           singurul lucru de aici de care cineva are nevoie in
                           mijlocul noptii. */
                        <i className="fas fa-life-ring text-muted" title="Has a written workaround"></i>
                      )}
                    </td>
                    <td>
                      <ItemCell row={row} />
                    </td>
                    <td>
                      <span className="adm-pill" style={{ background: row.statusColour, color: '#fff' }}>
                        {row.statusLabel}
                      </span>
                    </td>
                    <td className="center">{row.incidents}</td>
                    <td className="text-muted">{row.created.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-muted small">
              {data.problems.length} of {data.total}.
            </p>
          </>
        )}
      </div>
    </Content>
  )
}

function ItemCell({ row }: { row: ProblemRow }) {
  if (row.ciName !== '') {
    return (
      <a href={`/app/cmdb/view/${row.ciId}`} target="_blank" rel="noopener">
        {row.ciName}
      </a>
    )
  }
  if (row.ciId > 0) {
    /*
     * Elementul a fost sters din CMDB. Se spune, nu se lasa coloana goala: o
     * analiza care si-a pierdut subiectul e o constatare, nu o absenta.
     *
     * `adm-pill-bad`, nu `adm-pill-danger` cum scria macheta veche: clasa aia nu
     * e definita NICAIERI in `adm.css`, deci insigna iesea neutra tocmai acolo
     * unde trebuia sa fie rosie. Culorile care exista sunt -ok, -warn, -bad si
     * -running.
     */
    return <span className="adm-pill adm-pill-bad">deleted item #{row.ciId}</span>
  }
  return <span className="text-muted">&mdash;</span>
}
